import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Stack,
  Alert,
  Divider,
  Chip,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { InventoryService } from '../../services/inventoryService'
import { StockInfo, StockAdjustmentType } from '../../types/inventory'

interface StockAdjustmentDialogProps {
  open: boolean
  stock: StockInfo | null
  onClose: () => void
  onSuccess: () => void
}

const StockAdjustmentDialog: React.FC<StockAdjustmentDialogProps> = ({
  open,
  stock,
  onClose,
  onSuccess,
}) => {
  const [adjustmentType, setAdjustmentType] = useState<StockAdjustmentType>(StockAdjustmentType.MANUAL)
  const [adjustmentMode, setAdjustmentMode] = useState<'set' | 'adjust'>('adjust')
  const [newStock, setNewStock] = useState('')
  const [adjustment, setAdjustment] = useState('')
  const [reason, setReason] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  React.useEffect(() => {
    if (stock && open) {
      setNewStock(stock.current_stock.toString())
      setAdjustment('')
      setReason('')
      setReferenceId('')
      setAdjustmentMode('adjust')
      setAdjustmentType(StockAdjustmentType.MANUAL)
    }
  }, [stock, open])

  const handleSubmit = async () => {
    if (!stock) return

    if (!reason.trim()) {
      enqueueSnackbar('Please provide a reason for the stock adjustment', { variant: 'error' })
      return
    }

    setIsSubmitting(true)
    try {
      if (adjustmentMode === 'set') {
        const newStockValue = parseInt(newStock)
        if (isNaN(newStockValue) || newStockValue < 0) {
          enqueueSnackbar('Please enter a valid stock quantity', { variant: 'error' })
          return
        }

        await InventoryService.updateStock({
          variant_id: stock.variant_id,
          new_stock: newStockValue,
          adjustment_type: adjustmentType,
          reason: reason.trim(),
          reference_id: referenceId.trim() || undefined,
        })
      } else {
        const adjustmentValue = parseInt(adjustment)
        if (isNaN(adjustmentValue) || adjustmentValue === 0) {
          enqueueSnackbar('Please enter a valid adjustment amount', { variant: 'error' })
          return
        }

        await InventoryService.adjustStock({
          variant_id: stock.variant_id,
          adjustment: adjustmentValue,
          adjustment_type: adjustmentType,
          reason: reason.trim(),
          reference_id: referenceId.trim() || undefined,
        })
      }

      enqueueSnackbar('Stock adjusted successfully', { variant: 'success' })
      onSuccess()
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Failed to adjust stock',
        { variant: 'error' }
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const calculateNewStock = () => {
    if (!stock || adjustmentMode === 'set') return null
    const adjustmentValue = parseInt(adjustment) || 0
    return stock.current_stock + adjustmentValue
  }

  const newStockValue = calculateNewStock()

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Adjust Stock Level
        </Typography>
        {stock && (
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {stock.product_name} - {stock.variant_name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {stock && (
          <Stack spacing={3}>
            {/* Current Stock Info */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Stock
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {stock.current_stock.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reserved
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {stock.reserved_stock.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Available
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {stock.available_stock.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Min Stock
                  </Typography>
                  <Typography variant="body2">
                    {stock.minimum_stock.toLocaleString()}
                  </Typography>
                </Box>
                {stock.is_low_stock && (
                  <Chip label="Low Stock" color="warning" size="small" />
                )}
              </Stack>
            </Box>

            {/* Adjustment Mode */}
            <TextField
              select
              label="Adjustment Mode"
              value={adjustmentMode}
              onChange={(e) => setAdjustmentMode(e.target.value as 'set' | 'adjust')}
              fullWidth
            >
              <MenuItem value="adjust">Adjust by Amount</MenuItem>
              <MenuItem value="set">Set New Stock Level</MenuItem>
            </TextField>

            {/* Stock Input */}
            {adjustmentMode === 'set' ? (
              <TextField
                label="New Stock Level"
                type="number"
                value={newStock}
                onChange={(e) => setNewStock(e.target.value)}
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Set the new stock level for this variant"
              />
            ) : (
              <TextField
                label="Adjustment Amount"
                type="number"
                value={adjustment}
                onChange={(e) => setAdjustment(e.target.value)}
                fullWidth
                helperText="Enter positive number to increase, negative to decrease"
                InputProps={{
                  startAdornment: adjustment && parseInt(adjustment) > 0 ? '+' : '',
                }}
              />
            )}

            {/* New Stock Preview */}
            {newStockValue !== null && (
              <Alert severity="info">
                New stock level will be: <strong>{newStockValue.toLocaleString()}</strong>
                {newStockValue < 0 && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    Warning: This will result in negative stock!
                  </Typography>
                )}
              </Alert>
            )}

            {/* Adjustment Type */}
            <TextField
              select
              label="Adjustment Type"
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value as StockAdjustmentType)}
              fullWidth
            >
              <MenuItem value={StockAdjustmentType.MANUAL}>Manual Adjustment</MenuItem>
              <MenuItem value={StockAdjustmentType.PURCHASE}>Purchase/Restock</MenuItem>
              <MenuItem value={StockAdjustmentType.SALE}>Sale</MenuItem>
              <MenuItem value={StockAdjustmentType.RETURN}>Return</MenuItem>
              <MenuItem value={StockAdjustmentType.DAMAGE}>Damage/Loss</MenuItem>
              <MenuItem value={StockAdjustmentType.CORRECTION}>Stock Correction</MenuItem>
              <MenuItem value={StockAdjustmentType.TRANSFER}>Transfer</MenuItem>
            </TextField>

            {/* Reason */}
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
              helperText="Provide a reason for this stock adjustment"
            />

            {/* Reference ID */}
            <TextField
              label="Reference ID (Optional)"
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              fullWidth
              helperText="Order ID, invoice number, or other reference"
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={isSubmitting}
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={isSubmitting || !stock}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            minWidth: 120,
          }}
        >
          {isSubmitting ? 'Adjusting...' : 'Adjust Stock'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StockAdjustmentDialog